import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { HttpStatusCode } from "axios";
import { getApiErrorStatus, getErrorMessageFromUnknown } from "_shared/_errors/api-error-message";
import { api, getApiEndpointName } from "_shared/_stores/api";
import { logSchemaFailure } from "_shared/_utilities/log-schema-failure";
import Guid from "_shared/guids/guid";
import VendorRolloutBoardSchema, {
  VendorRolloutBoardJSON,
} from "legal-entities/vendor-rollouts/_schema/vendor-rollout-board-schema";

/**
 * Which roll-outs to report on: one client's firms, or one firm's clients.
 *
 * Omitting both ids asks for every client at once, which is the staff question and needs the
 * platform grant. Neither is ever a shorthand for "mine": the API decides scope from what was asked
 * for rather than from who is asking, so a client- or firm-facing surface must always send its own
 * id. Sending both is refused with a 400 — the board reports on one organization at a time, and
 * answering one while dropping the other would return a wider set than was asked for.
 */
export type VendorRolloutBoardRequest = {
  clientLegalEntityId?: Guid;
  vendorLegalEntityId?: Guid;
  includeClosed?: boolean;
};

export const vendorRolloutBoardApi = api.injectEndpoints({
  endpoints: (build) => ({
    getVendorRolloutBoard: build.query<VendorRolloutBoardJSON, VendorRolloutBoardRequest>({
      query: ({ clientLegalEntityId, vendorLegalEntityId, includeClosed }) => {
        const parameters = new URLSearchParams();
        if (clientLegalEntityId) parameters.set("clientLegalEntityId", clientLegalEntityId.value);
        if (vendorLegalEntityId) parameters.set("vendorLegalEntityId", vendorLegalEntityId.value);
        if (includeClosed) parameters.set("includeClosed", "true");
        const queryString = parameters.toString();
        return queryString ? `legal-entities/vendor-rollouts?${queryString}` : "legal-entities/vendor-rollouts";
      },
      rawResponseSchema: VendorRolloutBoardSchema.VendorRolloutBoardJSONZ,
      onSchemaFailure: logSchemaFailure,
      providesTags: ["VendorRolloutBoard"],
      transformErrorResponse: (response: FetchBaseQueryError, meta) => toVendorRolloutBoardError(response, meta),
    }),
  }),
});

export const { useGetVendorRolloutBoardQuery } = vendorRolloutBoardApi;

/**
 * The caller asked for a set they may not read — another client's roll-outs, or every client's
 * without the platform grant. Distinct from a general failure because the page says something
 * different: there is nothing to retry.
 */
export class UnauthorizedVendorRolloutBoardError extends Error {
  public constructor() {
    super("You do not have permission to see these roll-outs.");
  }
}

export class VendorRolloutBoardAPIError extends Error {
  public methodName?: string;
  public error: unknown;

  public constructor(methodName: string | undefined, error: unknown) {
    const endpointName = methodName ? `.${methodName}` : "";
    super(`Error in vendorRolloutBoardApi${endpointName}: ${getErrorMessageFromUnknown(error) ?? "An error occurred"}`);
    this.methodName = methodName;
    this.error = error;
  }
}

export function isUnauthorizedVendorRolloutBoardError(error: unknown): boolean {
  return error instanceof UnauthorizedVendorRolloutBoardError;
}

function toVendorRolloutBoardError(response: FetchBaseQueryError, meta?: unknown): Error {
  if (getApiErrorStatus(response) === HttpStatusCode.Forbidden) {
    return new UnauthorizedVendorRolloutBoardError();
  }
  return new VendorRolloutBoardAPIError(getApiEndpointName(meta), response);
}
